import type { ElasticSearchFilters, PCAReportRequestPayload, PcaReportFilters } from 'c-sdk';
import { EsPCAReportFilterField } from 'c-sdk';

export type { PcaReportFilters };

/** The schedule filter every PCA has always used when nothing narrows it. */
export const ALL_SCHEDULES = ['IN', 'OUT', 'UNBOOKED'];

const filterFields = Object.values(EsPCAReportFilterField) as string[];

const isFilterField = (key: string): key is EsPCAReportFilterField => filterFields.includes(key);

/**
 * The payload's `filters`, which arrive from the URL: only known report fields, each with at least
 * one string value. Anything else is dropped rather than trusted.
 */
export const parsePcaReportFilters = (raw: unknown): PcaReportFilters => {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};

    return Object.fromEntries(
        Object.entries(raw).flatMap(([key, values]) => {
            if (!isFilterField(key) || !Array.isArray(values)) return [];
            const strings = values.filter((v): v is string => typeof v === 'string');
            return strings.length > 0 ? [[key, strings]] : [];
        }),
    );
};

/**
 * The `fieldFilters` for a `ReportsElasticSearch` request (the SDK adds the `filters.` prefix):
 * `base` with the payload's filters on top, so a narrowed schedule replaces the default one.
 */
export const esFieldFilters = (
    filters: PcaReportFilters | null | undefined,
    base: PcaReportFilters = { [EsPCAReportFilterField.Schedule]: ALL_SCHEDULES },
): ElasticSearchFilters => ({ ...base, ...(filters ?? {}) });

/**
 * The `fieldFilters` for `Reports.pcaReport`, which posts them as the body unchanged. With
 * `timeFrame: 'summary'` that hits `pcareport/:id/summary`, which reads `filters.<column>` keys,
 * not the `PCAReportField` keys the SDK type describes; hence the one cast.
 */
export const pcaReportFieldFilters = (
    filters: PcaReportFilters | null | undefined,
): PCAReportRequestPayload['fieldFilters'] =>
    Object.fromEntries(
        Object.entries(esFieldFilters(filters)).map(([key, values]) => [`filters.${key}`, values]),
    ) as PCAReportRequestPayload['fieldFilters'];
